import React, { useCallback, useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// -----------------------------------------------------------------------
// Founder Inbox (2026-08-28)
//
// Native, in-app notifications for high-signal marketing events (starting
// with waitlist "story" submissions).  Replaces Slack/SMS: Roger reads
// stories here, marks them read, and clicks through to reply by hand.
// -----------------------------------------------------------------------

function fmtDate(v) {
  if (!v) return "\u2014";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return String(v);
  }
}

function KindLabel({ kind }) {
  if (kind === "waitlist_story") {
    return <Pill tone="warn">Waitlist story</Pill>;
  }
  return <Pill tone="muted">{kind || "notification"}</Pill>;
}

export default function FounderInbox() {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (unreadOnly) params.set("unread_only", "true");
      const r = await api.get(`/inbox?${params.toString()}`);
      setRows(r.data.rows || []);
      setUnread(r.data.unread || 0);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId],
  );

  const markRead = async (id) => {
    setBusy(true);
    try {
      await api.post(`/inbox/${id}/read`);
      setRows((prev) =>
        prev.map((r) =>
          r.id === id && !r.read_at
            ? { ...r, read_at: new Date().toISOString() }
            : r,
        ),
      );
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post(`/inbox/mark-all-read`);
      const now = new Date().toISOString();
      setRows((prev) =>
        prev.map((r) => (r.read_at ? r : { ...r, read_at: now })),
      );
      setUnread(0);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Founder Inbox">
      <div className="bo-page" data-testid="founder-inbox">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Marketing &middot; Notifications</div>
          <h1
            style={{
              fontFamily: "var(--bo-serif)",
              fontSize: 28,
              margin: "4px 0 4px 0",
            }}
          >
            Founder Inbox
          </h1>
          <p
            style={{
              color: "var(--bo-muted)",
              margin: 0,
              fontSize: 14,
            }}
          >
            Where the waitlist finds you. Stories, notes, and high-signal
            marketing events land here so you can reply by hand.
          </p>
        </div>

        <div
          className="bo-card"
          style={{
            padding: 14,
            marginBottom: 12,
            display: "flex",
            gap: 16,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
          data-testid="inbox-summary"
        >
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <SummaryStat label="Unread" value={unread} tone="warn" />
            <SummaryStat label="Total" value={total} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <label
              style={{
                display: "inline-flex",
                gap: 6,
                alignItems: "center",
                fontSize: 13,
                color: "var(--bo-muted)",
              }}
            >
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                data-testid="inbox-toggle-unread-only"
              />
              Unread only
            </label>
            <button
              className="bo-btn bo-btn--ghost"
              onClick={load}
              disabled={loading}
              data-testid="inbox-refresh"
              style={{ padding: "8px 14px" }}
            >
              {loading ? "Loading\u2026" : "Refresh"}
            </button>
            <button
              className="bo-btn bo-btn--primary"
              onClick={markAllRead}
              disabled={busy || unread === 0}
              data-testid="inbox-mark-all-read"
              style={{ padding: "8px 14px" }}
            >
              Mark all read
            </button>
          </div>
        </div>

        {error && <ErrorBanner error={error} testId="inbox-error" />}
        {loading && <LoadingCard />}
        {!loading && rows.length === 0 && (
          <EmptyState
            title="Nothing new"
            description="When someone joins the waitlist and leaves a note, it lands here."
            testId="inbox-empty"
          />
        )}

        {!loading && rows.length > 0 && (
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: selected ? "minmax(320px, 1fr) 2fr" : "1fr",
            }}
          >
            <div
              className="bo-card"
              style={{ padding: 0, overflow: "hidden" }}
              data-testid="inbox-list"
            >
              {rows.map((r) => {
                const isSel = r.id === selectedId;
                const isRead = !!r.read_at;
                return (
                  <button
                    key={r.id}
                    onClick={() => {
                      setSelectedId(r.id);
                      if (!isRead) markRead(r.id);
                    }}
                    data-testid={`inbox-row-${r.id}`}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "14px 16px",
                      background: isSel
                        ? "var(--bo-accent-tint, #eef4ff)"
                        : isRead
                        ? "var(--bo-panel, #fff)"
                        : "#fffdf5",
                      border: 0,
                      borderTop: "1px solid var(--bo-border, #e6e2d8)",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      {!isRead && (
                        <span
                          aria-hidden
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#c48411",
                            display: "inline-block",
                          }}
                        />
                      )}
                      <KindLabel kind={r.kind} />
                      <span
                        style={{
                          fontSize: 11,
                          color: "var(--bo-muted)",
                          marginLeft: "auto",
                        }}
                      >
                        {fmtDate(r.created_at)}
                      </span>
                    </div>
                    <div
                      style={{
                        fontWeight: isRead ? 500 : 600,
                        fontSize: 14,
                        color: "var(--bo-ink)",
                        marginBottom: 2,
                      }}
                    >
                      {r.subject || "(no subject)"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--bo-muted)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.body || r.email || ""}
                    </div>
                  </button>
                );
              })}
            </div>

            {selected && (
              <div
                className="bo-card"
                style={{ padding: 24 }}
                data-testid="inbox-detail"
              >
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <KindLabel kind={selected.kind} />
                  {selected.product_display && (
                    <Pill tone="muted">{selected.product_display}</Pill>
                  )}
                  {!selected.read_at ? (
                    <Pill tone="warn">Unread</Pill>
                  ) : (
                    <Pill tone="success">Read</Pill>
                  )}
                </div>
                <h2
                  style={{
                    fontFamily: "var(--bo-serif)",
                    fontSize: 22,
                    margin: "6px 0 4px 0",
                  }}
                  data-testid="inbox-detail-subject"
                >
                  {selected.subject || "(no subject)"}
                </h2>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bo-muted)",
                    marginBottom: 14,
                  }}
                >
                  {selected.person_name ? `${selected.person_name} · ` : ""}
                  <a
                    href={`mailto:${selected.email}`}
                    style={{
                      color: "var(--bo-accent, #234a8b)",
                      textDecoration: "none",
                    }}
                  >
                    {selected.email}
                  </a>{" "}
                  · {fmtDate(selected.created_at)}
                </div>

                <div
                  style={{
                    background: "#faf5ec",
                    border: "1px solid var(--bo-border, #e6e2d8)",
                    borderRadius: 8,
                    padding: 16,
                    fontFamily: "var(--bo-serif)",
                    fontSize: 15,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                  data-testid="inbox-detail-body"
                >
                  {selected.body || "(no body)"}
                </div>

                <div
                  style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}
                >
                  {selected.cta_href && (
                    <a
                      className="bo-btn bo-btn--primary"
                      href={selected.cta_href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="inbox-detail-cta"
                      style={{ padding: "10px 18px" }}
                    >
                      {selected.cta_label || "Reply"}
                    </a>
                  )}
                  {!selected.read_at && (
                    <button
                      className="bo-btn bo-btn--ghost"
                      onClick={() => markRead(selected.id)}
                      disabled={busy}
                      data-testid="inbox-detail-mark-read"
                      style={{ padding: "10px 18px" }}
                    >
                      Mark read
                    </button>
                  )}
                </div>

                {selected.meta && Object.keys(selected.meta).length > 0 && (
                  <details
                    style={{ marginTop: 20 }}
                    data-testid="inbox-detail-meta"
                  >
                    <summary
                      style={{
                        cursor: "pointer",
                        color: "var(--bo-muted)",
                        fontSize: 12,
                      }}
                    >
                      Metadata
                    </summary>
                    <pre
                      style={{
                        marginTop: 6,
                        padding: 12,
                        background: "var(--bo-panel-alt, #f6f4ef)",
                        borderRadius: 6,
                        fontSize: 11,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(selected.meta, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}

function SummaryStat({ label, value, tone }) {
  const color =
    tone === "success"
      ? "#1f6c3f"
      : tone === "warn"
      ? "#8a5a0a"
      : tone === "muted"
      ? "var(--bo-muted)"
      : "var(--bo-ink)";
  return (
    <div>
      <div
        className="bo-eyebrow"
        style={{ fontSize: 11, color: "var(--bo-muted)" }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--bo-serif)",
          fontSize: 24,
          color,
          fontWeight: 600,
        }}
        data-testid={`inbox-summary-${label.toLowerCase()}`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}
